import { useState } from "react";
import { useGameStore } from "../../store/gameStore";
import { useUIStore } from "../../store/uiStore";
import { useApi } from "../../hooks/useApi";
import { BUILDING_ICONS, BUILDING_NAMES } from "../city/BuildingSlot";
import { Timer } from "../shared/Timer";
import { hapticFeedback, hapticNotification } from "../../telegram/init";
import { getBuildingCost, getBuildingTime, BUILDING_DEFINITIONS, BuildingType } from "@ikarium/shared";

const BUILDABLE_TYPES = Object.values(BuildingType);

export function BuildingPanel() {
  const closePanel = useUIStore((s) => s.closePanel);
  const position = useUIStore((s) => s.selectedBuildingPosition);
  const cities = useGameStore((s) => s.cities);
  const activeCityId = useGameStore((s) => s.activeCityId);
  const updateCity = useGameStore((s) => s.updateCity);
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const city = cities.find((c) => c.id === activeCityId);
  if (!city || position === null) return null;

  const building = city.buildings.find((b) => b.position === position);
  const isEmpty = !building;

  async function handleUpgrade() {
    if (!activeCityId || position === null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.upgradeBuilding(activeCityId, position);
      hapticNotification("success");
      // Refresh city data
      const cityData = await api.getCity(activeCityId);
      updateCity({ id: activeCityId, ...cityData });
      closePanel();
    } catch (e: any) {
      setError(e.message);
      hapticNotification("error");
    } finally {
      setLoading(false);
    }
  }

  async function handleBuild(buildingType: string) {
    if (!activeCityId || position === null) return;
    setLoading(true);
    setError(null);
    try {
      await api.buildBuilding(activeCityId, position, buildingType);
      hapticNotification("success");
      const cityData = await api.getCity(activeCityId);
      updateCity({ id: activeCityId, ...cityData });
      closePanel();
    } catch (e: any) {
      setError(e.message);
      hapticNotification("error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={closePanel} />
      <div className="panel-sheet">
        <div className="panel-handle" />

        {isEmpty ? (
          /* — Build new building — */
          <>
            <h2>Construct Building</h2>
            <p style={{
              textAlign: "center",
              color: "var(--ink-faded)",
              marginBottom: "var(--space-lg)",
              fontSize: "0.9rem",
            }}>
              Choose a building for slot {position + 1}
            </p>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "var(--space-sm)",
            }}>
              {BUILDABLE_TYPES.filter((bt) =>
                !city.buildings.some((b) => b.buildingType === bt)
              ).map((bt) => {
                const def = BUILDING_DEFINITIONS[bt];
                const cost = getBuildingCost(bt, 1);
                const canAfford = city.wood >= cost.wood && city.marble >= cost.marble;
                return (
                  <button
                    key={bt}
                    disabled={loading || !canAfford}
                    onClick={() => handleBuild(bt)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                      padding: "var(--space-md)",
                      borderRadius: "var(--radius)",
                      background: canAfford
                        ? "linear-gradient(135deg, var(--marble), var(--papyrus-light))"
                        : "var(--papyrus-dark)",
                      border: "1px solid var(--sand)",
                      opacity: canAfford ? 1 : 0.5,
                      boxShadow: canAfford ? "var(--shadow-soft)" : "none",
                    }}
                  >
                    <span style={{ fontSize: "1.3rem" }}>
                      {BUILDING_ICONS[bt] ?? "\u{1F3E0}"}
                    </span>
                    <span style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "0.6rem",
                      fontWeight: 600,
                      color: "var(--ink)",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}>
                      {def.name}
                    </span>
                    <span style={{
                      fontSize: "0.65rem",
                      color: "var(--ink-faded)",
                    }}>
                      {"\u{1FAB5}"}{cost.wood} {"\u2B1C"}{cost.marble}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          /* — Existing building info — */
          <>
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--space-sm)",
              marginBottom: "var(--space-lg)",
            }}>
              <span style={{ fontSize: "2.5rem" }}>
                {BUILDING_ICONS[building.buildingType] ?? "\u{1F3E0}"}
              </span>
              <h2 style={{ margin: 0 }}>
                {BUILDING_NAMES[building.buildingType] ?? building.buildingType}
              </h2>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-sm)",
              }}>
                <span style={{
                  background: "var(--terra)",
                  color: "var(--papyrus-light)",
                  fontFamily: "var(--font-display)",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  padding: "2px 10px",
                  borderRadius: "10px",
                }}>
                  Level {building.level}
                </span>
                {building.isUpgrading && building.upgradeEndAt && (
                  <Timer endAt={building.upgradeEndAt} />
                )}
              </div>
            </div>

            {/* Description */}
            <p style={{
              textAlign: "center",
              color: "var(--ink-faded)",
              marginBottom: "var(--space-lg)",
              fontSize: "0.9rem",
              fontStyle: "italic",
            }}>
              {BUILDING_DEFINITIONS[building.buildingType as BuildingType]?.description ?? ""}
            </p>

            {/* Upgrade cost */}
            {!building.isUpgrading && (
              <>
                <div style={{
                  background: "var(--parchment)",
                  borderRadius: "var(--radius)",
                  padding: "var(--space-md)",
                  marginBottom: "var(--space-lg)",
                }}>
                  <div style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "var(--ink-faded)",
                    marginBottom: "var(--space-sm)",
                  }}>
                    Upgrade to Level {building.level + 1}
                  </div>
                  <CostDisplay
                    buildingType={building.buildingType}
                    level={building.level + 1}
                    city={city}
                  />
                  <div style={{
                    marginTop: "var(--space-sm)",
                    fontSize: "0.8rem",
                    color: "var(--ink-faded)",
                  }}>
                    Build time: {formatTime(getBuildingTime(building.buildingType as BuildingType, building.level + 1))}
                  </div>
                </div>

                <button
                  className="btn-primary"
                  disabled={loading || city.buildings.some((b) => b.isUpgrading)}
                  onClick={handleUpgrade}
                  style={{ width: "100%", padding: "14px" }}
                >
                  {loading ? "Upgrading..." : city.buildings.some((b) => b.isUpgrading) ? "Queue Busy" : "Upgrade"}
                </button>
              </>
            )}

            {building.isUpgrading && (
              <div style={{
                textAlign: "center",
                padding: "var(--space-lg)",
                background: "linear-gradient(135deg, rgba(201,169,97,0.1), rgba(218,183,82,0.05))",
                borderRadius: "var(--radius)",
                border: "1px solid rgba(201,169,97,0.3)",
              }}>
                <div style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "0.75rem",
                  color: "var(--gold-dim)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}>
                  Under Construction
                </div>
                <div style={{ marginTop: "var(--space-sm)" }}>
                  <Timer endAt={building.upgradeEndAt!} />
                </div>
              </div>
            )}
          </>
        )}

        {error && (
          <div style={{
            marginTop: "var(--space-md)",
            padding: "var(--space-md)",
            background: "rgba(196, 91, 58, 0.1)",
            border: "1px solid rgba(196, 91, 58, 0.3)",
            borderRadius: "var(--radius)",
            color: "var(--terra-dark)",
            fontSize: "0.85rem",
            textAlign: "center",
          }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}

function CostDisplay({ buildingType, level, city }: {
  buildingType: string;
  level: number;
  city: { wood: number; marble: number; crystal: number; sulfur: number };
}) {
  const cost = getBuildingCost(buildingType as BuildingType, level);
  const items = [
    { icon: "\u{1FAB5}", label: "Wood",    amount: cost.wood,    has: city.wood },
    { icon: "\u2B1C",    label: "Marble",  amount: cost.marble,  has: city.marble },
    { icon: "\u{1F48E}", label: "Crystal", amount: cost.crystal, has: city.crystal },
    { icon: "\u{1F525}", label: "Sulfur",  amount: cost.sulfur,  has: city.sulfur },
  ].filter((i) => i.amount > 0);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-sm)" }}>
      {items.map((item) => (
        <span key={item.label} style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          fontSize: "0.85rem",
          fontWeight: 600,
          color: item.has >= item.amount ? "var(--olive)" : "var(--terra)",
        }}>
          {item.icon} {Math.floor(item.amount).toLocaleString()}
        </span>
      ))}
    </div>
  );
}

function formatTime(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
